// src/components/Quiz/QuizTypeFormation/index.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext'; // Adjust path
import { normalizedQuizQuestions } from '../quizQuestions'; // Adjust path
import apiCall from '../../utils/api'; // Adjust path
import useDeviceType from '../useDeviceType'; // Adjust path
import ProgressBar from '../ProgressBar'; // Import ProgressBar
import FlecheEnvoiClair from '../../assets/fleche_envoi_clair.png'; // Import back button image
import DontKnowIcon from '../../assets/dont_know_icon.png';

// Import styles to potentially access ANIMATION_DURATION if defined there,
// otherwise keep the constant here.
import {
  Container,
  QuizTopHeader,
  ReturnButton,
  ContentWrapper,
  LogoBubbleRow,
  LogoImage,
  ChatBubbleContainer,
  ChatText, // Use the styled component
  AnswersContainer,
  AnswerGrid,
  AnswerOptionCard,
  JspButton,
  LoadingOverlay,
  QuizMessage,
  ProgressBarWrapper // Add ProgressBarWrapper to imports from styles
} from './styles'; // Make sure styles.js has the blink animation and other fixes

import Meo from '../../assets/meo_quiz.png'; // Adjust path

const TYPING_SPEED = 15;
// const ANIMATION_DURATION = 500; // Old duration, no longer used for cards
const CARD_PRESS_DURATION = 150; // Time the card stays visually pressed (ms)
const ALL_CARDS_DISAPPEAR_DURATION = 300; // Duration of all cards disappearing (ms)
const SHADOW_RETURN_ANIMATION_DURATION = 100; // Duration for shadow to reappear (ms), matches CSS transition

const JSP_INDEX = -1; // Constant for JSP answer

const QuizTypeFormation = ({ onQuizComplete }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useDeviceType(); // Keep if used elsewhere, not directly used in the logic shown

  const totalQuestions = normalizedQuizQuestions.length;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState(Array(totalQuestions).fill(null));
  const [isLoading, setIsLoading] = useState(false); // For API calls
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [error, setError] = useState('');
  const [text, setText] = useState(''); // Current displayed text in bubble
  const [isTyping, setIsTyping] = useState(true); // Controls typing effect & disabling inputs
  // const [animatingOutIndex, setAnimatingOutIndex] = useState(null); // Old state
  // const [fadingOutIndices, setFadingOutIndices] = useState([]); // Old state
  const [pressedButton, setPressedButton] = useState(null); // For card press effect (index) or JSP ('jsp')
  const [areCardsDisappearing, setAreCardsDisappearing] = useState(false); // New state for simultaneous disappearance

  // Check quiz status on mount
  useEffect(() => {
    const checkStatus = async () => {
      if (!isAuthenticated) {
        setIsCheckingStatus(false);
        return;
      }
      try {
        const response = await apiCall(`${process.env.REACT_APP_BACKEND_URL}/quiz/status/`, 'GET');
        if (response.completed) {
          localStorage.setItem('quiz1Completed', 'true');
          navigate('/classement'); // Redirect if already completed
        } else {
          localStorage.setItem('quiz1Completed', 'false');
        }
      } catch (err) {
        console.error('Failed to fetch quiz status:', err);
        // Decide what to do on error - maybe allow quiz to proceed
      } finally {
        setIsCheckingStatus(false);
      }
    };
    checkStatus();
  }, [isAuthenticated, navigate]);

  // Load saved answers on initial mount
  useEffect(() => {
    if (isCheckingStatus) return; // Wait for status check to complete
    const savedAnswers = localStorage.getItem('quiz1Answers');
    let initialIndex = 0;
    let parsedAnswers = Array(totalQuestions).fill(null);
    try {
      const parsed = savedAnswers ? JSON.parse(savedAnswers) : null;
      if (Array.isArray(parsed) && parsed.length === totalQuestions) {
        parsedAnswers = parsed;
        const firstUnanswered = parsed.findIndex(ans => ans === null);
        initialIndex = firstUnanswered === -1 ? (totalQuestions > 0 ? totalQuestions - 1 : 0) : firstUnanswered;
      }
    } catch (e) {
        console.error("Failed to parse saved answers:", e);
        localStorage.removeItem('quiz1Answers'); // Clear corrupted data
    }
    setUserAnswers(parsedAnswers);
    setCurrentQuestionIndex(initialIndex);
    setText('');
    setIsTyping(true);
  }, [totalQuestions, isCheckingStatus]);

  // Save answers to localStorage whenever they change
  useEffect(() => {
    if (userAnswers.some(answer => answer !== null)) {
      localStorage.setItem('quiz1Answers', JSON.stringify(userAnswers));
    }
  }, [userAnswers]);

  const currentQuestion = normalizedQuizQuestions[currentQuestionIndex];
  const fullText = currentQuestion?.text || "";

  // Typing effect logic
  useEffect(() => {
    let typingInterval;
    if (isTyping && fullText) {
      let currentIndex = 0;
      let currentTextVal = ''; 
      setText(''); 
      typingInterval = setInterval(() => {
        if (currentIndex < fullText.length) {
          currentTextVal += fullText.charAt(currentIndex);
          setText(currentTextVal); 
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, TYPING_SPEED);
    } else if (!isTyping && text !== fullText) {
        setText(fullText);
    }
    return () => clearInterval(typingInterval);
  }, [isTyping, fullText]);

  // submitQuiz should now take the answers as an argument
  const submitQuiz = useCallback(async (answersToSubmit) => {
    // No longer uses userAnswers directly from state for submission payload

    // ---- START DETAILED DEBUG LOG ----
    console.log('[SUBMIT QUIZ] Received answersToSubmit:', JSON.stringify(answersToSubmit));
    console.log('[SUBMIT QUIZ] answersToSubmit.length:', answersToSubmit ? answersToSubmit.length : 'undefined/null');
    console.log('[SUBMIT QUIZ] Expected totalQuestions:', totalQuestions);
    if (answersToSubmit && typeof totalQuestions === 'number' && totalQuestions > 0 && answersToSubmit.length === totalQuestions) {
        console.log('[SUBMIT QUIZ] Value of last answer (answersToSubmit[totalQuestions - 1]):', JSON.stringify(answersToSubmit[totalQuestions - 1]));
    } else if (answersToSubmit && typeof totalQuestions === 'number') {
        console.warn('[SUBMIT QUIZ] Mismatch or issue with answersToSubmit length or totalQuestions. Last element (if exists):', answersToSubmit[answersToSubmit.length - 1]);
    } else {
        console.error('[SUBMIT QUIZ] answersToSubmit is problematic or totalQuestions is not a valid number.');
    }
    // ---- END DETAILED DEBUG LOG ----

    if (isLoading) return;
    console.log('Submitting answers:', answersToSubmit);
    setIsLoading(true);
    setError('');
    try {
      await apiCall(`${process.env.REACT_APP_BACKEND_URL}/quiz/submit-all-answers/`, 'POST', { answers: answersToSubmit });
      localStorage.removeItem('quiz1Answers');
      localStorage.setItem('quiz1Completed', 'true');
      if (onQuizComplete) onQuizComplete();
      localStorage.setItem('need_update_ranking_formation', 'true');
      localStorage.setItem('need_update_ranking', 'true');
      navigate("/classement");
      setIsLoading(false); // setLoading to false only after navigation
    } catch (err) {
      console.error('Error submitting quiz answers:', err);
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la soumission du quiz. Veuillez réessayer.';
      setError(errorMsg);
      setIsLoading(false);
    }
    // Dependencies: isLoading, navigate, onQuizComplete. userAnswers is removed.
  }, [navigate, onQuizComplete, isLoading]);

  const handleNextQuestion = useCallback(() => {
    // Reset animation states
    setPressedButton(null);
    setAreCardsDisappearing(false);

    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
      setText(''); 
      setIsTyping(true); 
    } else {
      // This case should ideally not be hit if selectAnswerRevised handles submission
      console.warn("[HANDLE NEXT QUESTION] Reached end of quiz, but submission should have been handled by selectAnswerRevised.");
      // As a fallback, maybe do nothing or ensure UI is not stuck.
      // Or, if submitQuiz absolutely must be callable from here for some edge case:
      // submitQuiz(userAnswers); // but this reintroduces the stale state risk for last answer.
    }
    // userAnswers removed from dependencies if not used for a fallback submitQuiz call
  }, [currentQuestionIndex, totalQuestions /*, submitQuiz */ ]);

  // Corrected selectAnswer timing logic - This will be the new primary selectAnswer function
  const selectAnswerRevised = (answerIndex) => {
    // Guard: if already animating out, or other interaction lock
    if (areCardsDisappearing || pressedButton !== null || isTyping || isLoading) {
      return;
    }

    // Create the new answers array immediately
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;

    // Update the state (this will trigger re-render and useEffect for localStorage)
    setUserAnswers(newAnswers);

    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

    if (answerIndex === JSP_INDEX) {
      setPressedButton('jsp'); // For JSP active style

      setTimeout(() => {
        setPressedButton(null); // Release JSP active style
        setAreCardsDisappearing(true); // Trigger disappearance of answer cards

        setTimeout(() => {
          if (isLastQuestion) {
            submitQuiz(newAnswers);
          } else {
            handleNextQuestion(); // This will set areCardsDisappearing to false
          }
        }, ALL_CARDS_DISAPPEAR_DURATION);
      }, CARD_PRESS_DURATION); // JSP button also held "pressed" for a bit
      return;
    }

    // Regular Answer Card
    setPressedButton(answerIndex); // 1. Card is visually pressed (shadow disappears)

    setTimeout(() => { // Timeout A: Runs after CARD_PRESS_DURATION
      setPressedButton(null); // 2. Card visually un-presses (shadow STARTS to come back)

      setTimeout(() => { // Timeout B: Runs after shadow has reappeared
        setAreCardsDisappearing(true);

        setTimeout(() => { // Timeout C: Runs after cards have disappeared
          if (isLastQuestion) {
            submitQuiz(newAnswers);
          } else {
            handleNextQuestion(); // This will set areCardsDisappearing to false
          }
        }, ALL_CARDS_DISAPPEAR_DURATION);
      }, SHADOW_RETURN_ANIMATION_DURATION); // Wait for shadow to return

    }, CARD_PRESS_DURATION); // Initial press duration
  };

  const handlePrevious = () => {
    // if (isTyping || isLoading || animatingOutIndex !== null || fadingOutIndices.length > 0) return;
    if (isTyping || isLoading || areCardsDisappearing || pressedButton !== null) return; // Updated guard

    if (currentQuestionIndex > 0) {
      // setAnimatingOutIndex(null); // Old state
      // setFadingOutIndices([]);   // Old state, remove if not used elsewhere
      setPressedButton(null);      // Ensure pressed button is cleared
      setAreCardsDisappearing(false); // Ensure disappearing state is cleared
      setCurrentQuestionIndex(prevIndex => prevIndex - 1);
      setText('');
      setIsTyping(true);
    }
  };
  
  const generalUIDisabled = isTyping || isLoading;
  // const answerCardsDisabled = isTyping || isLoading || animatingOutIndex !== null || fadingOutIndices.length > 0;
  const answerCardsDisabled = isTyping || isLoading || areCardsDisappearing || pressedButton !== null;
  // const navigationButtonsDisabled = isTyping || isLoading || animatingOutIndex !== null || fadingOutIndices.length > 0;
  const navigationButtonsDisabled = isTyping || isLoading || areCardsDisappearing || pressedButton !== null;

  // Display message if user is not authenticated
  if (!isAuthenticated) {
    return <Container><QuizMessage isError={false}>Veuillez vous connecter pour accéder au quiz.</QuizMessage></Container>;
  }

  // Show a loading indicator while checking the quiz status
  if (isCheckingStatus) {
    return (
      <Container>
        <LoadingOverlay isLoading={true} />
      </Container>
    );
  }

  // Show loading overlay if waiting for API response (submit)
  // Or potentially if questions were being fetched initially (add state if needed)
   if (isLoading && !currentQuestion && currentQuestionIndex < totalQuestions) {
       return (
           <Container>
               <LoadingOverlay isLoading={true} />
           </Container>
       );
   }
   if (!currentQuestion) {
    // Handle cases where currentQuestion is null/undefined after initial checks
     console.error("Critical Error: currentQuestion is null or undefined at render time.", currentQuestionIndex);
     return <Container><QuizMessage isError={true}>Une erreur critique est survenue. Impossible d'afficher la question.</QuizMessage></Container>;
  }

  const progressPercent = Math.min(100, ((currentQuestionIndex + 1) / totalQuestions) * 100);
  const isFirstQuestion = currentQuestionIndex === 0;
  // Determine if any animation that blocks UI interaction is active
  return (
    <Container isMobile={isMobile}>
      {/* Show loading overlay specifically during final submission process */}
      <LoadingOverlay isLoading={isLoading && currentQuestionIndex >= totalQuestions -1 && userAnswers.every(a => a !== null) } />
      
      <QuizTopHeader>
         <ReturnButton 
           onClick={handlePrevious} 
           disabled={generalUIDisabled || isFirstQuestion || navigationButtonsDisabled}
           $isHidden={isFirstQuestion}
         >
           <img src={FlecheEnvoiClair} alt="Back" style={{ width: '24px', height: '24px' }} />
         </ReturnButton>
         <ProgressBarWrapper $isFirstQuestion={isFirstQuestion}>
           <ProgressBar startPercent={0} endPercent={progressPercent} />
         </ProgressBarWrapper>
       </QuizTopHeader>

      <ContentWrapper>
        <LogoBubbleRow>
          <LogoImage src={Meo} alt="Meoria Quiz Mascot" />
          <ChatBubbleContainer>
            <ChatText>
              <span>{text}</span>
              {isTyping && <span className="typing-cursor">|</span>}
            </ChatText>
          </ChatBubbleContainer>
        </LogoBubbleRow>

        <AnswersContainer>
          <AnswerGrid $isDisabled={answerCardsDisabled}> {/* Pass $isDisabled prop */}
            {currentQuestion.answers.map((answerText, index) => (
              <AnswerOptionCard
                key={`${currentQuestionIndex}-${index}`}
                index={index}
                onClick={() => selectAnswerRevised(index)} // Use the revised function
                disabled={answerCardsDisabled}
                // $isAnimatingOut={animatingOutIndex === index} // Old prop
                // $isFadingOut={fadingOutIndices.includes(index)} // Old prop
                $areDisappearing={areCardsDisappearing} // New prop
                $isLeftSide={index % 2 === 0} // Keep if used by styles not shown, otherwise can be removed if only for old cardSelectAnimation
                active={pressedButton === index} // For card's own press effect, compares with card index
              >
                {answerText}
              </AnswerOptionCard>
            ))}
          </AnswerGrid>
        </AnswersContainer>

        <JspButton
          onClick={() => selectAnswerRevised(JSP_INDEX)} // Changed to onClick
          // onMouseDown={handleJspPressStart} // Removed
          // onMouseUp={handleJspPressEnd} // Removed
          // onMouseLeave={handleJspPressCancel} // Removed
          // onTouchStart={handleJspPressStart} // Removed
          // onTouchEnd={handleJspPressEnd} // Removed
          disabled={generalUIDisabled || navigationButtonsDisabled} // This existing disabled logic should still work
          active={pressedButton === 'jsp'} // JSP button uses 'jsp' string for its active state
        >
          <img src={DontKnowIcon} alt="Je ne sais pas" />
        </JspButton>

        {error && <QuizMessage isError={true}>{error}</QuizMessage>}
      </ContentWrapper>
    </Container>
  );
};

export default QuizTypeFormation;